import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Score as ScoreIcon,
  Equalizer as StatsIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  ErrorOutline,
  CheckCircle
} from '@mui/icons-material';

const PerformanceStatusChip = ({ status }) => {
    switch(status) {
      case 'High Concern':
        return <Chip icon={<ErrorOutline />} label={status} color="error" />;
      case 'Moderate Concern':
        return <Chip icon={<WarningIcon />} label={status} color="warning" />;
      default:
        return <Chip icon={<CheckCircle />} label={status} color="success" />;
    }
  };

export const LowPerformanceDialog = ({onClose, open, tutor}) => {
    if (!tutor) return null;
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="span">
              {tutor.fullname}
            </Typography>
            <Box flexGrow={1} />
            <PerformanceStatusChip status={tutor.performance_status} />
          </Box>
          <Typography variant="subtitle2" color="textSecondary">
            Tutor ID: {tutor.id}
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CalendarIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary={`${tutor.session_count} sessions`}
                secondary={`Percentile: ${Math.round(tutor.session_percentile * 100)}%`}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <SchoolIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary={`${tutor.unique_student_count} students`}
                secondary={`Percentile: ${Math.round(tutor.student_percentile * 100)}%`}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <ScoreIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary={`Average score: ${tutor.avg_student_score ? Math.round(tutor.avg_student_score) + '%' : 'N/A'}`}
                secondary="Across all assessments"
              />
            </ListItem>
            
            <Divider component="li" sx={{ my: 1 }} />
            
            <ListItem>
              <ListItemIcon>
                <StatsIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Performance Analysis"
                secondary={
                  tutor.performance_status === 'High Concern' 
                    ? 'This tutor is in the bottom quartile for both sessions and students'
                    : tutor.performance_status === 'Moderate Concern'
                      ? 'This tutor is in the bottom quartile for either sessions or students'
                      : 'Performance meets expectations'
                }
              />
            </ListItem>
          </List>
  
          {tutor.recent_sessions && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Recent Session Activity
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                {tutor.recent_sessions.map((session, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={new Date(session.date).toLocaleDateString()}
                      secondary={`${session.student_count} students`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={onClose} 
            startIcon={<CloseIcon />}
            sx={{ mr: 1 }}
          >
            Close
          </Button>

        </DialogActions>
      </Dialog>
    );
  };